import {
  SecondaryContainer as CommonContainer,
  Pagination,
  NoContent,
  Loading,
} from "components";
import styled from "styled-components";
import { generateImage } from "utils/newFunction";
import { responsive } from "utils/theme";
import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import Image from "components/Image";
import { ApiService } from "services/client";
import { Formatter } from "utils/formatter";
import { toast } from "react-toastify";
import Toast from "components/Toast/Toast";
import {
  persianToGregorian,
  persianDateTimeToGregorian,
  getDifferenceDateTime,
  isWarningDateAfterCurrentDate,
} from "./tools";
import { useLocale } from "hooks";
import useCountdown from "hooks/useCountdown";
import DateUtils from "utils/DateUtils";

const SIZE = 6;

interface CommonContainerProps {
  children: React.ReactNode;
}

// Types
interface CardData {
  id: string;
  title: string | JSX.Element[];
  image: string;
  link: string;
  alt: string;
  enableLabel: boolean;
  color: string;
  label: string;
  gregorianDate: string;
  gregorianWarningDate: string;
}

interface NewsEventCardProps {
  v: any;
  className?: string;
  size?: "small" | "large";
  single?: boolean;
}

interface LabeledListRendererProps {
  researchcentersTagTree: string | null;
  pagination?: boolean;
}

interface PaginationClickEvent {
  selected: number;
}

// Helper Functions
const formatCardData = (item: any, locale: string): CardData => {
  let label = "";
  const gregorianDate = item?.date?.value
    ? persianToGregorian(item?.date?.value, "YYYY-MM-DD", locale)
    : "";
  const gregorianWarningDate = item?.warningDate?.value
    ? persianDateTimeToGregorian(item?.warningDate?.value, "YYYY-MM-DD", locale)
    : "";
  const difference = getDifferenceDateTime(item?.date?.value);
  const splittedDifference = difference.split(":").map(Number);

  // Label logic
  let isExpired = false;
  if (isWarningDateAfterCurrentDate(item?.date?.value)) {
    isExpired = true;
  }

  if (item?.label?.value) {
    label = item?.label?.value;
  } else if (isExpired) {
    label = locale === "fa" ? "پایان سررسید" : "Expired";
  } else if (splittedDifference[0] > 0) {
    label = `${
      splittedDifference[0] === 1
        ? locale === "fa"
          ? "یک"
          : "One"
        : splittedDifference[0]
    } ${
      locale === "fa"
        ? "روز تا سررسید"
        : `Day${splittedDifference[0] === 1 ? "" : "s"} left`
    }`;
  } else if (splittedDifference[0] === 0) {
    label = splittedDifference.slice(1).join(":");
  }

  return {
    id: item?.id,
    title:
      item?.["ResearchTitle"]?.value
        ?.split?.(" ")
        ?.map?.((word: string, i: number, arr: string[]) =>
          Number.isNaN(+word) ? (
            arr?.length === i + 1 ? (
              word
            ) : (
              `${word} `
            )
          ) : (
            <bdi key={i} className="ENGLISH_FONTS">{`${word} `}</bdi>
          )
        ) ?? "",
    image: generateImage(item?.["ResearchImage2"]?.value),
    link: item?.["Research_Internallink"]?.value || "#",
    alt: item?.imageTag?.value || item?.["ResearchTitle"]?.value || "",
    enableLabel:
      typeof item?.enableLabel?.value === "boolean" &&
      Boolean(item?.enableLabel?.value),
    color: isExpired
      ? "#E71313"
      : isWarningDateAfterCurrentDate(item?.warningDate?.value)
      ? "#E71313"
      : "#07BC0C",
    label,
    gregorianDate: gregorianDate?.toLocaleDateString?.("en-US") || "",
    gregorianWarningDate:
      gregorianWarningDate?.toLocaleDateString?.("en-US") || "",
  };
};

// Components
const NewsEventCard = ({
  v,
  className,
  size = "small",
  single = false,
}: NewsEventCardProps) => {
  const { locale } = useLocale();
  const data = useMemo(() => formatCardData(v, locale), [v, locale]);
  const countDown = useCountdown(data?.label);

  return (
    <Link href={data.link} prefetch={false}>
      <a
        id={`card-${data?.id}`}
        className={clsx(
          "tw-flex tw-w-full tw-flex-col tw-group tw-m-0 tw-relative tw-overflow-hidden",
          className,
          {
            "sm:tw-w-1/2 tw-mx-auto": single,
            "lg:tw-w-2/3": size === "large",
            "lg:tw-w-[calc(100%/3)]": size !== "large",
          }
        )}
      >
        {data?.enableLabel && (
          <div
            style={{ rotate: "-45deg", background: data?.color }}
            dir={countDown.includes(":") ? "ltr" : "rtl"}
            className={clsx(
              "tw-text-center tw-absolute tw-z-50 tw-px-10 tw-text-[#FFFF] tw-text-base tw-font-bold tw-w-[90%] tw-left-[-30%] tw-top-[15%] tw-flex tw-items-center tw-justify-center",
              { "tw-h-[35px]": Boolean(countDown) }
            )}
          >
            {countDown?.replaceAll?.(":", " : ")}
          </div>
        )}
        <CardImage size={size} data={data} />
        <CardTitle title={data.title} />
      </a>
    </Link>
  );
};

const CardImage = ({
  size,
  data,
}: {
  size: "small" | "large";
  data: CardData;
}) => (
  <div
    className={clsx("tw-w-full", { "md:tw-aspect-[6/2]": size === "large" })}
  >
    <div
      className={clsx(
        "tw-relative tw-rounded-lg tw-h-full tw-overflow-hidden",
        {
          "tw-aspect-[3/2]": size !== "large",
          "tw-aspect-[6/2]": size === "large",
        }
      )}
    >
      <Image
        width={size === "small" ? 300 : 600}
        height={200}
        src={data?.image}
        alt={data?.alt}
        className="tw-transition-all tw-rounded-lg xxxl:tw-rounded-[10.67px] tw-w-full tw-h-full tw-object-cover"
      />
      <div className="tw-rounded-lg tw-text-lg xxxl:tw-rounded-[10.67px] tw-font-medium tw-absolute tw-top-0 tw-w-full tw-h-full tw-flex tw-bg-gradient-to-b tw-items-end tw-pr-5 tw-pb-4 gradiant" />
    </div>
  </div>
);

const CardTitle = ({ title }: { title: string | JSX.Element[] }) => (
  <span className="tw-text-[#344456] tw-text-base xxxl:tw-text-[21.33px] tw-font-bold tw-mt-4 group-hover:tw-text-[#007FAC] tw-transition-all tw-min-h-[50px] xxxl:tw-mt-5 xxxl:tw-leading-[36.17px] title">
    {title}
  </span>
);

const StyledNewsEventCard = styled(NewsEventCard)`
  .title {
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
  .gradiant {
    background: linear-gradient(
      180deg,
      rgba(52, 68, 86, 0) 66.67%,
      rgba(52, 68, 86, 0.7) 100%
    );
  }
  img {
    transition: transform 0.3s;
    @media (max-width: ${responsive.md}px) {
      transform: scaleY(1.06);
    }
    &:hover {
      transform: scale(1.1);
    }
  }
`;

function LabeledListRenderer({
  researchcentersTagTree = null,
  pagination = true,
}: LabeledListRendererProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPagesCount, setTotalPagesCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [list1Count, setList1Count] = useState(0);
  const [list1Pages, setList1Pages] = useState(0);
  const [displayData, setDisplayData] = useState<CardData[]>([]);

  // Move useMemo before any conditional returns
  const chunks = useMemo(() => {
    const result: CardData[][] = [];
    let clone = [...displayData];
    while (clone.length) {
      result.push(clone.splice(0, 3));
    }
    return result;
  }, [displayData]);

  const mobileChunks = useMemo(() => {
    const result: CardData[][] = [];
    let clone = [...displayData];
    while (clone.length) {
      result.push(clone.splice(0, 2));
    }
    return result;
  }, [displayData]);

  useEffect(() => {
    if (researchcentersTagTree) {
      initializeData();
    }
  }, [researchcentersTagTree]);

  const initializeData = async () => {
    try {
      setIsLoading(true);
      const apiService = new ApiService("fa", true);

      const baseParams = {
        tagTrees: researchcentersTagTree ? [researchcentersTagTree] : [],
        fieldCode: ["date"],
        fieldValue: [
          DateUtils.p2e(
            new Date()
              .toLocaleString("fa-IR", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
              })
              ?.replaceAll(",", "")
          ),
        ],
        orderBy: ["content.date:desc"],
        operationValuesArray: [] as string[],
        operationFields: "and",
        format: false,
      };

      // Get counts for both lists at initialization
      const [resp1, resp2] = await Promise.all([
        apiService.getContent(
          {
            ...baseParams,
            operationValuesArray: [...baseParams.operationValuesArray, "gte"],
            size: 1,
          },
          false
        ),
        apiService.getContent(
          {
            ...baseParams,
            operationValuesArray: [...baseParams.operationValuesArray, "lt"],
            size: 1,
          },
          false
        ),
      ]);

      const count1 = resp1?.count || 0;
      setList1Count(count1);

      const pages1 = Math.ceil(count1 / SIZE);
      setList1Pages(pages1);

      const pages2 = Math.ceil((resp2?.count || 0) / SIZE);
      setTotalPagesCount(pages1 + pages2);

      // Load first page data
      loadPageData(1, count1, pages1);
    } catch (error) {
      toast.error("خطایی رخ داد");
    }
  };

  const loadPageData = async (
    page: number,
    count1 = list1Count,
    pages1 = list1Pages
  ) => {
    try {
      setIsLoading(true);
      const apiService = new ApiService("fa", true);

      const baseParams = {
        tagTrees: researchcentersTagTree ? [researchcentersTagTree] : [],
        fieldCode: ["date"],
        fieldValue: [
          DateUtils.p2e(
            new Date()
              .toLocaleString("fa-IR", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
              })
              ?.replaceAll(",", "")
          ),
        ],
        orderBy: ["content.date:desc"],
        operationValuesArray: [] as string[],
        operationFields: "and",
        size: SIZE,
        format: false,
      };

      const isFirstList = page <= pages1;

      const response = await apiService.getContent(
        {
          ...baseParams,
          operationValuesArray: [
            ...baseParams.operationValuesArray,
            isFirstList ? "gte" : "lt",
          ],
          offset: isFirstList ? (page - 1) * SIZE : (page - pages1 - 1) * SIZE,
        },
        false
      );

      setDisplayData(Formatter.formatData({ data: response }) || []);
    } catch (error) {
      toast.error("خطایی رخ داد");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageClick = async (d: PaginationClickEvent) => {
    const newPage = d?.selected + 1;
    setCurrentPage(newPage);
    await loadPageData(newPage);
  };

  return (
    <CommonContainer as="div">
      <Toast />
      {isLoading ? (
        <div className="tw-flex tw-justify-center tw-items-center">
          <Loading />
        </div>
      ) : (
        <>
          {/* Desktop View */}
          <div className="tw-hidden lg:tw-block tw-px-4 md:tw-px-0">
            {chunks?.length > 0 ? (
              chunks.map((chunk, i) => (
                <div
                  key={i}
                  className={clsx(
                    "tw-mb-[30px] last:tw-mb-0 tw-gap-[30px] tw-flex-wrap lg:tw-flex-nowrap",
                    {
                      "md:tw-inline-flex lg:tw-flex tw-justify-center": true,
                      "tw-mx-auto md:tw-px-[30px]": chunk?.length === 2,
                    }
                  )}
                >
                  {chunk.map((item) => (
                    <StyledNewsEventCard
                      key={item?.id}
                      v={item}
                      size={chunk?.length === 1 ? "large" : "small"}
                    />
                  ))}
                </div>
              ))
            ) : (
              <NoContent />
            )}
          </div>

          {/* Mobile View */}
          <div className="tw-block lg:tw-hidden tw-px-4 md:tw-px-0">
            {mobileChunks?.length > 0 ? (
              mobileChunks.map((chunk, i) => (
                <div
                  key={i}
                  className={clsx("tw-mb-[30px] last:tw-mb-0 tw-gap-[30px]", {
                    "tw-flex tw-mx-auto lg:tw-px-[30px]": chunk?.length < 2,
                    "tw-grid tw-grid-cols-1 sm:tw-grid-cols-2":
                      chunk?.length === 2,
                  })}
                >
                  {chunk.map((item) => (
                    <StyledNewsEventCard
                      key={item?.id}
                      v={item}
                      single={chunk?.length === 1}
                    />
                  ))}
                </div>
              ))
            ) : (
              <NoContent />
            )}
          </div>

          {pagination && (
            <div className="tw-mt-5">
              <Pagination
                currentPage={currentPage}
                handlePageClick={handlePageClick}
                totalPagesCount={totalPagesCount}
              />
            </div>
          )}
        </>
      )}
    </CommonContainer>
  );
}

export default LabeledListRenderer;
